import { Record, List } from "immutable";
import { DateStringNumber, Nullable } from "common/records";
import { IEstimateLineItemData } from "common/records/EstimateLineItem";
import { IRoomEstimationData, RoomEstimationRecord, roomEstimationFromJSON } from "common/records/RoomEstimation";
import { checkDate } from "common/helpers/Date";

export const estimateGroupFromJSON = (json: Partial<IEstimateGroupData>): EstimateGroupRecord => {
  const record: IEstimateGroupRecord = { ...(json as any) };

  if (json.line_items) {
    record.line_item_ids = List(json.line_items.map((li) => li.id));
  }

  if (json.room_estimations) {
    record.room_estimations = List(json.room_estimations.map((estimation) => roomEstimationFromJSON(estimation)));
  }

  if (json.created_at) {
    record.created_at = checkDate(json.created_at);
  }

  if (json.updated_at) {
    record.updated_at = checkDate(json.updated_at);
  }

  return new EstimateGroupRecord(record);
};

export const sumEstimateGroupTotals = (estimateGroups: List<Nullable<EstimateGroupRecord>>) => {
  return estimateGroups.reduce(
    (totals, estimateGroupRecord) => {
      if (!estimateGroupRecord) {
        console.error("Missing required data in redux to sum estimate line item totals", {
          estimateGroups: estimateGroups.toJS(),
        });
        return totals;
      }

      if (estimateGroupRecord._destroy || !estimateGroupRecord.included) {
        return totals;
      }

      const extPrice = estimateGroupRecord.subtotal || 0;
      const laborPrice = estimateGroupRecord.labor_total || 0;
      const productPrice = estimateGroupRecord.product_total || 0;
      const discountableTotal = estimateGroupRecord.discountable_total || 0;

      return {
        subtotal: totals.subtotal + extPrice,
        discountable_total: totals.discountable_total + discountableTotal,
        labor_total: totals.labor_total + laborPrice,
        product_total: totals.product_total + productPrice,
      };
    },
    {
      subtotal: 0,
      discountable_total: 0,
      labor_total: 0,
      product_total: 0,
    },
  );
};

export interface IEstimateGroupData {
  id: number;
  name: string;
  included: boolean;
  subtotal: number;
  total: number;
  labor_total: number;
  product_total: number;
  discountable_total: number;
  line_items: IEstimateLineItemData[];
  room_estimations: IRoomEstimationData[];
  estimate_id: number;
  created_at: DateStringNumber;
  updated_at: DateStringNumber;
  sort_order: number;
  _destroy: boolean;
  product_markup: Nullable<number>;
  labor_markup: Nullable<number>;
}

export interface IEstimateGroupRecord {
  id: number;
  name: string;
  included: boolean;
  subtotal: number;
  total: number;
  labor_total: number;
  product_total: number;
  discountable_total: number;
  line_item_ids: List<number>;
  room_estimations: List<RoomEstimationRecord>;
  estimate_id: number;
  created_at: Date;
  updated_at: Date;
  sort_order: Nullable<number>;
  _destroy: Nullable<boolean>;
  product_markup: Nullable<number>;
  labor_markup: Nullable<number>;
}

const defaultEstimateGroupProps: IEstimateGroupRecord = {
  id: 0,
  name: "",
  included: true,
  subtotal: 0,
  total: 0,
  labor_total: 0,
  product_total: 0,
  discountable_total: 0,
  line_item_ids: List<number>(),
  room_estimations: List<RoomEstimationRecord>(),
  estimate_id: 0,
  created_at: new Date(),
  updated_at: new Date(),
  sort_order: null,
  _destroy: null,
  product_markup: 0,
  labor_markup: 0,
};

export class EstimateGroupRecord extends Record(defaultEstimateGroupProps) implements IEstimateGroupRecord {}
